/**
 * @fileoverview Workflow Roles manager (MOR-748)
 *
 * Firm-defined workflow roles (e.g. Partner, Manager, Analyst) that workflow
 * template tasks can be assigned to instead of a fixed user. Who fills each
 * role is chosen per job at creation (or from job settings), so one workflow
 * serves every team. Admin/owner only — `canEdit` gates writes; mutations
 * enforce the same rule.
 *
 * Visual language mirrors the Task Statuses manager for consistency.
 */

import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { api } from "@cvx/_generated/api";
import { extractErrorMessage } from "@/utils/errors";
import type { Id } from "@cvx/_generated/dataModel";
import type { TaskColor } from "@cvx/lib/taskColor.schema";
import type { WorkflowRoleIcon } from "@cvx/tasks/workflowRoles/schema";
import { WORKFLOW_ROLE_DESCRIPTION_MAX_LENGTH as ROLE_DESCRIPTION_MAX_LENGTH } from "@cvx/tasks/roleFields";
import {
  ArrowDown,
  ArrowUp,
  GripVertical,
  Loader2,
  Lock,
  Pencil,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { Badge, Button } from "@/ui/primitives";
import { Input } from "@/ui/primitives/input";
import { Label } from "@/ui/primitives/label";
import { cn } from "@/utils/misc";
import { ColorSwatchPicker } from "./ColorSwatchPicker";
import { IconSwatchPicker } from "./IconSwatchPicker";
import { RoleBadge } from "./RoleBadge";

const GROUP_CARD_CLASS =
  "rounded-xl bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.1),0_2px_4px_rgba(0,0,0,0.06)] transition-shadow duration-200 overflow-hidden";

interface WorkflowRole {
  _id: Id<"workflowRoles">;
  key: string;
  name: string;
  color: TaskColor;
  icon?: WorkflowRoleIcon;
  description?: string;
  sortOrder: number;
}

export function WorkflowRolesManager({
  orgId,
  canEdit,
}: {
  orgId: Id<"organizations">;
  canEdit: boolean;
}) {
  const { data: roles } = useQuery(
    convexQuery(api.tasks.workflowRoles.listByOrg, { orgId }),
  );
  const reorder = useConvexMutation(api.tasks.workflowRoles.reorder);
  const [showNewForm, setShowNewForm] = useState(false);
  const rows: WorkflowRole[] = useMemo(() => roles ?? [], [roles]);

  const orderedRows = useMemo(
    () => [...rows].sort((a, b) => a.sortOrder - b.sortOrder),
    [rows],
  );

  // Drag-to-reorder (mirrors the statuses manager). A 6px activation
  // distance keeps row clicks/buttons usable; only the grip starts a drag.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const ids = orderedRows.map((r) => r._id);
    const from = ids.indexOf(active.id as Id<"workflowRoles">);
    const to = ids.indexOf(over.id as Id<"workflowRoles">);
    if (from < 0 || to < 0) return;
    reorder({ orgId, orderedIds: arrayMove(ids, from, to) }).catch(
      (err: Error) => toast.error(err.message || "Could not reorder roles."),
    );
  };

  return (
    <div className="flex w-full flex-col gap-6">
      {!canEdit && (
        <div className="flex items-start gap-3 rounded-xl border border-amber-200/60 bg-amber-50 px-4 py-3 text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200">
          <Lock className="mt-0.5 size-4 shrink-0" />
          <div className="text-sm">
            <p className="font-medium">Read-only view</p>
            <p className="mt-0.5 text-xs opacity-80">
              You need owner or admin access to manage workflow roles.
            </p>
          </div>
        </div>
      )}

      <div className={GROUP_CARD_CLASS}>
        <div className="flex items-start justify-between gap-6 border-b border-border/50 p-6">
          <div className="min-w-0 flex-1">
            <h1 className="text-lg font-semibold text-primary">
              Workflow Roles
            </h1>
            <p className="mt-1 text-sm text-primary/60">
              Roles like Partner, Manager or Analyst that workflow tasks can be
              assigned to. Pick who fills each role when a job is created — the
              same workflow then works for every team.
            </p>
          </div>
          <Badge variant="secondary" className="shrink-0">
            {rows.length} {rows.length === 1 ? "role" : "roles"}
          </Badge>
        </div>

        <div className="flex items-center justify-between border-b border-border/50 px-6 py-4">
          <div>
            <h4 className="text-sm font-semibold text-primary">Roles</h4>
            <p className="text-xs text-primary/40">
              Drag to reorder — this is the order they appear in pickers.
            </p>
          </div>
          {canEdit && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowNewForm(true)}
              className="gap-1.5"
            >
              <Plus className="size-3.5" />
              Add role
            </Button>
          )}
        </div>

        <div className="divide-y divide-border/30">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={orderedRows.map((r) => r._id)}
              strategy={verticalListSortingStrategy}
            >
              {orderedRows.map((role, index) => (
                <RoleRow
                  key={role._id}
                  orgId={orgId}
                  role={role}
                  canEdit={canEdit}
                  isFirst={index === 0}
                  isLast={index === orderedRows.length - 1}
                  orderedIds={orderedRows.map((r) => r._id)}
                  flatIndex={index}
                />
              ))}
            </SortableContext>
          </DndContext>

          {rows.length === 0 && !showNewForm && (
            <div className="px-6 py-8 text-center">
              <p className="text-sm text-primary/40">
                No workflow roles yet.
                {canEdit
                  ? " Add roles like Partner, Manager or Analyst to assign workflow tasks by role."
                  : ""}
              </p>
            </div>
          )}

          {showNewForm && (
            <NewRoleForm orgId={orgId} onClose={() => setShowNewForm(false)} />
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Role rows
// ---------------------------------------------------------------------------

function RoleRow({
  orgId,
  role,
  canEdit,
  isFirst,
  isLast,
  orderedIds,
  flatIndex,
}: {
  orgId: Id<"organizations">;
  role: WorkflowRole;
  canEdit: boolean;
  isFirst: boolean;
  isLast: boolean;
  orderedIds: Array<Id<"workflowRoles">>;
  flatIndex: number;
}) {
  const [editing, setEditing] = useState(false);
  const update = useConvexMutation(api.tasks.workflowRoles.update);
  const reorder = useConvexMutation(api.tasks.workflowRoles.reorder);
  const archive = useConvexMutation(api.tasks.workflowRoles.archive);

  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: role._id, disabled: !canEdit || editing });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const move = (direction: -1 | 1) => {
    const next = [...orderedIds];
    const target = flatIndex + direction;
    if (target < 0 || target >= next.length) return;
    [next[flatIndex], next[target]] = [next[target], next[flatIndex]];
    reorder({ orgId, orderedIds: next }).catch((e: Error) =>
      toast.error(e.message || "Could not reorder roles."),
    );
  };

  if (editing) {
    return (
      <div ref={setNodeRef} style={style} className="px-6 py-4">
        <RoleForm
          initial={{
            name: role.name,
            color: role.color,
            icon: role.icon ?? null,
            description: role.description ?? "",
          }}
          submitLabel="Save"
          onCancel={() => setEditing(false)}
          onSubmit={async (state) => {
            await update({
              roleId: role._id,
              patch: {
                name: state.name.trim(),
                color: state.color,
                // null clears the icon; "" clears the description.
                icon: state.icon,
                description: state.description.trim(),
              },
            });
            setEditing(false);
          }}
        />
      </div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "flex items-center gap-3 bg-background px-6 py-3",
        isDragging && "relative z-10 shadow-lg",
      )}
    >
      {canEdit && (
        <span
          ref={setActivatorNodeRef}
          {...attributes}
          {...listeners}
          className="cursor-grab touch-none text-primary/25 hover:text-primary/60 active:cursor-grabbing"
          title="Drag to reorder"
          aria-label={`Drag ${role.name} to reorder`}
        >
          <GripVertical className="size-4" />
        </span>
      )}
      {canEdit && (
        <div className="flex flex-col">
          <button
            type="button"
            disabled={isFirst}
            onClick={() => move(-1)}
            className="text-primary/30 hover:text-primary/70 disabled:opacity-30"
            aria-label="Move up"
          >
            <ArrowUp className="size-3" />
          </button>
          <button
            type="button"
            disabled={isLast}
            onClick={() => move(1)}
            className="text-primary/30 hover:text-primary/70 disabled:opacity-30"
            aria-label="Move down"
          >
            <ArrowDown className="size-3" />
          </button>
        </div>
      )}
      <RoleBadge role={role} className="shrink-0" />
      <div className="min-w-0 flex-1">
        {role.description && (
          <p className="truncate text-xs text-primary/50">{role.description}</p>
        )}
      </div>
      {canEdit && (
        <div className="flex items-center gap-1">
          <Button
            size="icon"
            variant="ghost"
            className="size-7"
            onClick={() => setEditing(true)}
          >
            <Pencil className="size-3.5" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="size-7 text-destructive hover:text-destructive"
            onClick={() => {
              archive({ roleId: role._id }).catch((e: Error) =>
                toast.error(e.message || "Could not remove role."),
              );
            }}
          >
            <Trash2 className="size-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
}

function NewRoleForm({
  orgId,
  onClose,
}: {
  orgId: Id<"organizations">;
  onClose: () => void;
}) {
  const create = useConvexMutation(api.tasks.workflowRoles.create);
  return (
    <div className="bg-muted/20 px-6 py-4">
      <RoleForm
        initial={{ name: "", color: "gray", icon: null, description: "" }}
        submitLabel="Add role"
        onCancel={onClose}
        onSubmit={async (state) => {
          await create({
            orgId,
            name: state.name.trim(),
            color: state.color,
            icon: state.icon ?? undefined,
            description: state.description.trim() || undefined,
          });
          onClose();
        }}
      />
    </div>
  );
}

interface RoleFormState {
  name: string;
  color: TaskColor;
  icon: WorkflowRoleIcon | null;
  description: string;
}

function RoleForm({
  initial,
  submitLabel,
  onCancel,
  onSubmit,
}: {
  initial: RoleFormState;
  submitLabel: string;
  onCancel: () => void;
  onSubmit: (state: RoleFormState) => Promise<void>;
}) {
  const [state, setState] = useState<RoleFormState>(initial);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!state.name.trim()) {
      toast.error("Role name is required.");
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit(state);
    } catch (e) {
      toast.error(extractErrorMessage(e, "Could not save role."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1.5">
        <Label className="text-xs font-medium text-muted-foreground">
          Name
        </Label>
        <Input
          value={state.name}
          autoFocus
          onChange={(e) => setState({ ...state, name: e.target.value })}
          placeholder="Partner, Manager, Analyst…"
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label className="text-xs font-medium text-muted-foreground">
          Colour
        </Label>
        <ColorSwatchPicker
          value={state.color}
          onChange={(color) => setState({ ...state, color })}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label className="text-xs font-medium text-muted-foreground">
          Icon
        </Label>
        <IconSwatchPicker
          value={state.icon}
          onChange={(icon) => setState({ ...state, icon })}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label className="text-xs font-medium text-muted-foreground">
          Description
          <span className="ml-1 font-normal opacity-70">(optional)</span>
        </Label>
        <Input
          value={state.description}
          maxLength={ROLE_DESCRIPTION_MAX_LENGTH}
          onChange={(e) => setState({ ...state, description: e.target.value })}
          placeholder="What this role is responsible for"
        />
        <p className="text-xs text-muted-foreground">
          Shown as a tooltip wherever this role appears, so whoever is assigning
          it knows what it covers.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2 rounded-md bg-muted/30 px-3 py-2">
        <span className="text-xs text-muted-foreground">Preview</span>
        <RoleBadge
          role={{
            name: state.name.trim() || "Role name",
            color: state.color,
            icon: state.icon ?? undefined,
            description: state.description.trim() || undefined,
          }}
        />
        {/* Only promise a tooltip once there is a description to show. */}
        {state.description.trim() && (
          <span className="text-xs text-muted-foreground">
            Hover the badge to see the tooltip.
          </span>
        )}
      </div>
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onCancel} className="gap-1">
          <X className="size-3.5" />
          Cancel
        </Button>
        <Button
          size="sm"
          onClick={submit}
          disabled={submitting}
          className="gap-1.5"
        >
          {submitting ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Plus className="size-3.5" />
          )}
          {submitLabel}
        </Button>
      </div>
    </div>
  );
}
